import type * as React from 'react';

import { type DataQuery, type DataSourceJsonData } from '@grafana/schema';

import { type ScopedVars } from './ScopedVars';
import { type DataSourcePluginMeta, type DataSourceSettings } from './datasource';
import { type IconName } from './icon';
import { type PanelData } from './panel';
import { type AbsoluteTimeRange, type RawTimeRange, type TimeZone } from './time';

// Plugin Extensions types
// ---------------------------------------

export enum PluginExtensionTypes {
  link = 'link',
  component = 'component',
  function = 'function',
}

type PluginExtensionBase = {
  id: string;
  type: PluginExtensionTypes;
  title: string;
  description: string;
  pluginId: string;
};
export type PluginExtensionLink = PluginExtensionBase & {
  type: PluginExtensionTypes.link;
  path?: string;
  onClick?: (event?: React.MouseEvent) => void;
  icon?: IconName;
  /** @deprecated in favor of group property */
  category?: string;
  group?: { name: string; icon?: IconName };
  openInNewTab?: boolean;
};

export type PluginExtensionComponentMeta = Omit<PluginExtensionComponent, 'component'>;

export type PluginExtensionComponent<Props = {}> = PluginExtensionBase & {
  type: PluginExtensionTypes.component;
  component: React.ComponentType<Props>;
};

export type ComponentTypeWithExtensionMeta<Props = {}> = React.ComponentType<Props> & {
  meta: PluginExtensionComponentMeta;
};

export type PluginExtensionFunction<Signature = () => void> = PluginExtensionBase & {
  type: PluginExtensionTypes.function;
  fn: Signature;
};

export type PluginExtension = PluginExtensionLink | PluginExtensionComponent | PluginExtensionFunction;

// Objects used for registering extensions (in app plugins)
// --------------------------------------------------------

type PluginExtensionConfigBase = {
  /**
   * The title of the link extension
   */
  title: string;

  /**
   * A short description
   */
  description?: string;
};

export type PluginExtensionAddedComponentConfig<Props = {}> = PluginExtensionConfigBase & {
  /**
   * The target extension points where the component will be added
   */
  targets: string | string[];

  /**
   * The title of the component
   */
  title: string;

  /**
   * A short description of the component
   */
  description: string;

  /**
   * The React component that will added to the target extension points
   */
  component: React.ComponentType<Props>;
};

export type PluginExtensionAddedFunctionConfig<Signature = unknown> = PluginExtensionConfigBase & {
  /**
   * The target extension points where the component will be added
   */
  targets: string | string[];

  /**
   * The function to be executed
   */
  fn: Signature;
};

export type PluginAddedLinksConfigureFunc<Context extends object> = (context: Readonly<Context> | undefined) =>
  | Partial<{
      title: string;
      description: string;
      path: string;
      onClick: (event: React.MouseEvent | undefined, helpers: PluginExtensionEventHelpers<Context>) => void;
      icon: IconName;
      /** @deprecated in favor of group property */
      category?: string;
      group: { name: string; icon?: IconName };
      openInNewTab: boolean;
    }>
  | undefined;

export type PluginExtensionAddedLinkConfig<Context extends object = object> = PluginExtensionConfigBase & {
  /**
   * The target extension points where the link will be added
   */
  targets: string | string[];

  /** A URL path that will be used as the href for the rendered link extension
   * (It is optional, because in some cases the action will be handled by the `onClick` handler instead of navigating to a new page)
   */
  path?: string;

  /** A URL path that will be used as the href for the rendered link extension
   * (It is optional, because in some cases the action will be handled by the `onClick` handler instead of navigating to a new page)
   * path?: string;
   *
   * A function that will be called when the link is clicked
   *  (It is called with the original event object)
   */
  onClick?: (event: React.MouseEvent | undefined, helpers: PluginExtensionEventHelpers<Context>) => void;

  // (Optional) A function that can be used to configure the extension dynamically based on the extension point's context
  configure?: PluginAddedLinksConfigureFunc<Context>;

  // (Optional) A icon that can be displayed in the ui for the extension option.
  icon?: IconName;

  /** @deprecated in favor of group property */
  category?: string;

  // (Optional) A group to be used when grouping the options in the ui.
  group?: { name: string; icon?: IconName };

  // (Optional) If true, opens the link in a new tab (renders with target="_blank")
  // (Important: this is not guaranteed, depends on the extension point if it implements it.)
  openInNewTab?: boolean;
};

export type PluginExtensionExposedComponentConfig<Props = {}> = PluginExtensionConfigBase & {
  /**
   * The unique identifier of the component
   * Shoud be in the format of `<pluginId>/<componentName>/<componentVersion>`. e.g. `myorg-todo-app/todo-list/v1`
   */
  id: string;

  /**
   * The React component that will be exposed to other plugins
   */
  component: React.ComponentType<Props>;
};

export type PluginExtensionOpenModalOptions = {
  // The title of the modal
  title: string;
  // A React element that will be rendered inside the modal
  body: React.ElementType<{ onDismiss?: () => void }>;
  // Width of the modal in pixels or percentage
  width?: string | number;
  // Height of the modal in pixels or percentage
  height?: string | number;
};

export type PluginExtensionEventHelpers<Context extends object = object> = {
  context?: Readonly<Context>;
  // The ID of the extension point that triggered this event
  extensionPointId: string;
  // Opens a modal dialog and renders the provided React component inside it
  openModal: (options: PluginExtensionOpenModalOptions) => void;
  /**
   * @internal
   * Opens the extension sidebar with the registered component.
   * @param componentTitle The title of the component to be opened in the sidebar.
   * @param props The props to be passed to the component.
   */
  openSidebar: (componentTitle: string, props?: Record<string, unknown>) => void;
  /**
   * @internal
   * Closes the extension sidebar.
   */
  closeSidebar: () => void;
  /**
   * @internal
   * Toggles the extension sidebar with the registered component.
   * If the sidebar is open with the same component, it will be closed.
   * If the sidebar is closed or open with a different component, it will be opened with the specified component.
   * @param componentTitle The title of the component to be toggled in the sidebar.
   * @param props The props to be passed to the component.
   */
  toggleSidebar: (componentTitle: string, props?: Record<string, unknown>) => void;
};

// Extension Points & Contexts
// --------------------------------------------------------

// Extension Points available in core Grafana
export enum PluginExtensionPoints {
  AlertInstanceAction = 'grafana/alerting/instance/action',
  AlertingHomePage = 'grafana/alerting/home',
  AlertingAlertingRuleAction = 'grafana/alerting/alertingrule/action',
  AlertingRecordingRuleAction = 'grafana/alerting/recordingrule/action',
  AlertingRuleQueryEditor = 'grafana/alerting/alertingrule/queryeditor',
  CommandPalette = 'grafana/commandpalette/action',
  DashboardPanelMenu = 'grafana/dashboard/panel/menu',
  DashboardEmpty = 'grafana/dashboard/empty',
  DashboardSidebar = 'grafana/dashboard/sidebar/v1',
  DataSourceConfig = 'grafana/datasources/config',
  DataSourceConfigActions = 'grafana/datasources/config/actions',
  DataSourceConfigErrorStatus = 'grafana/datasources/config/error-status',
  DataSourceConfigStatus = 'grafana/datasources/config/status',
  ExploreToolbarAction = 'grafana/explore/toolbar/action',
  UserProfileTab = 'grafana/user/profile/tab',
  UserProfileMenu = 'grafana/user/profile/menu/v1',
  TraceViewDetails = 'grafana/traceview/details',
  TraceViewHeaderActions = 'grafana/traceview/header/actions',
  QueryEditorRowAdaptiveTelemetryV1 = 'grafana/query-editor-row/adaptivetelemetry/v1',
  QueryEditorRowActions = 'grafana/query-editor-row/actions/v1',
  TraceViewResourceAttributes = 'grafana/traceview/resource-attributes',
  LogsViewResourceAttributes = 'grafana/logsview/resource-attributes',
  AppChrome = 'grafana/app/chrome/v1',
  ExtensionSidebar = 'grafana/extension-sidebar/v0-alpha',
  MegaMenuAction = 'grafana/megamenu/action',
  SingleTopBarAction = 'grafana/singletopbar/action',
  AdvisorCompletedChecks = 'grafana/advisor/completed-checks/v1',
  AdvisorCreateChecks = 'grafana/advisor/create-checks/v1',
  AdvisorRetryCheck = 'grafana/advisor/retry-check/v1',
  NavRightButton = 'grafana/singletopbar/nav-right-button/v1',
  HomepageTabs = 'grafana/homepage/tabs/v1',
  HomepageAssistant = 'grafana/homepage/assistant/v1',
  HomepageExtra = 'grafana/homepage/extra/v1',
  UserListTab = 'grafana/admin/user-list/tab/v1',
}

// Don't use directly in a plugin!
// Extension point IDs that contain dynamic segments and are not valid as static values — they require runtime substitution of certain parts.
// (They cannot be used as is. E.g. "grafana/nav-landing-page/.*/v1" becomes "grafana/nav-landing-page/observability/v1" during runtime.)
//
// IMPORTANT: NavLandingPage and NavLandingPageCards are mutually exclusive.
// If a plugin extends NavLandingPage, it will replace the entire page content and any NavLandingPageCards extensions will be ignored.
// Only use NavLandingPageCards if you want to add additional cards to the default landing page layout.
export enum PluginExtensionPointPatterns {
  NavLandingPage = 'grafana/dynamic/nav-landing-page/nav-id-.*/v1',
  NavLandingPageCards = 'grafana/dynamic/nav-landing-page/nav-id-.*/cards/v1',
}

// Extension Points available in plugins
export enum PluginExtensionExposedComponents {
  CentralAlertHistorySceneV1 = 'grafana/central-alert-history-scene/v1',
  AddToDashboardFormV1 = 'grafana/add-to-dashboard-form/v1',
  PrometheusQueryResultsV1 = 'grafana/prometheus-query-results/v1',
  CreateAlertFromPanelV1 = 'grafana/alerting/create-alert-from-panel/v1',
  OpenQueryLibraryV1 = 'grafana/query-library-context/v1',
  NotebookViewV1 = 'grafana/notebook-view/v1',
}

export type PluginExtensionPanelContext = {
  pluginId: string;
  id: number;
  title: string;
  timeRange: RawTimeRange;
  timeZone: TimeZone;
  dashboard: Dashboard;
  targets: DataQuery[];
  scopedVars?: ScopedVars;
  data?: PanelData;
  /**
   * Path-based identifier for the rendered panel instance, unique even across
   * repeated panels (one per repeat instance, includes local variable values).
   *
   * Set only in scenes-based dashboards. Pass to
   * `getPanelScreenshotService().capture()` to capture the panel image.
   * Undefined in legacy dashboard contexts.
   */
  panelPathId?: string;
};

export type CentralAlertHistorySceneV1Props = {
  defaultLabelsFilter?: string;
  defaultTimeRange?: { from: string; to: string };
  hideFilters?: boolean;
  hideAlertRuleColumn?: boolean;
};

export type PluginExtensionQueryEditorRowAdaptiveTelemetryV1Context = {
  /** An ordered list of lower-case [a-z]+ string identifiers to provide context clues of where this component is being embedded and how we might want to consider displaying it */
  contextHints?: string[];
  query?: DataQuery & { expr?: string };
};

/**
 * The extension is rendered as an icon button next to the built-in query row actions, so it
 * should stay visually compact. A component that has nothing to contribute for the given query
 * (e.g. an unsupported datasource type) is expected to render `null`.
 */
export type PluginExtensionQueryEditorRowActionsV1Context = {
  query: DataQuery;
  /** All queries in the same query editor, including `query`. */
  queries?: DataQuery[];
  /** The datasource the query runs against. Undefined while the row is still resolving it. */
  dataSource?: {
    uid: string;
    type: string;
    name: string;
  };
  /** Which part of Grafana the query editor is rendered in, e.g. `dashboard`, `explore`. */
  app?: string;
  /** The time range the query is evaluated with, if the editor has one. */
  timeRange?: RawTimeRange;
};

export type PluginExtensionDataSourceConfigContext<
  JsonData extends DataSourceJsonData = DataSourceJsonData,
  SecureJsonData = {},
> = {
  // The current datasource settings
  dataSource: DataSourceSettings<JsonData>;

  // Meta information about the datasource plugin
  dataSourceMeta: DataSourcePluginMeta;

  // Testing status
  testingStatus?: {
    message?: string | null;
    status?: string | null;
  };

  // Can be used to update the `jsonData` field on the datasource
  // (Only updates the form, it still needs to be saved by the user)
  setJsonData: (jsonData: JsonData) => void;
  setSecureJsonData: (secureJsonData: SecureJsonData) => void;
};

export type PluginExtensionCommandPaletteContext = {};

export type PluginExtensionResourceAttributesContext = {
  // Key-value pairs of resource attributes, attribute name is the key
  attributes: Record<string, string[]>;
  spanAttributes?: Record<string, string[]>;
  timeRange: AbsoluteTimeRange;
  datasource: {
    type: string;
    uid: string;
  };
  traceID?: string;
  spanID?: string;
  spanStartTime?: number;
};

export type DataSourceConfigErrorStatusContext = {
  dataSource: {
    type: string;
    uid: string;
    name: string;
  };
  testingStatus: {
    message?: string | null;
    status?: string | null;
    details?: Record<string, unknown>;
  };
};

export type PluginExtensionDataSourceConfigActionsContext = {
  dataSource: {
    type: string;
    uid: string;
    name: string;
    typeName: string;
  };
};

export type PluginExtensionDataSourceConfigStatusContext = {
  dataSource: {
    type: string;
    uid: string;
    name: string;
    typeName: string;
  };
  testingStatus?: {
    message?: string | null;
    status?: string | null;
    details?: Record<string, unknown>;
  };
  severity: 'success' | 'error' | 'warning' | 'info';
};

type Dashboard = {
  uid: string;
  title: string;
  tags: string[];
};
